// 사용자 앱 웹 푸시 구독 클라이언트.
// 브라우저 PushManager 구독을 생성/해제하고 서버에 등록/해제한다.
//  - VAPID 공개키는 서버(/push/vapid-public-key)에서 받아 applicationServerKey 로 사용한다.
//  - 권한 거부/미지원 환경은 호출부가 메시지로 안내하도록 명확한 결과를 반환한다.

import { api } from "@/api/client.ts";

// 브라우저가 웹 푸시를 지원하는지.
export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// 현재 알림 권한 상태.
export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (!isPushSupported()) {
    return "unsupported";
  }
  return Notification.permission;
}

// base64url VAPID 공개키 -> Uint8Array (applicationServerKey 형식).
function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/gu, "+").replace(/_/gu, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

// 서버에서 VAPID 공개키를 받는다. 미설정이면 빈 문자열.
async function fetchVapidPublicKey(): Promise<string> {
  const data = await api.get<{ publicKey: string }>("/push/vapid-public-key", {
    withAuth: false,
  });
  return data.publicKey;
}

// 구독 결과.
export type SubscribeResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "denied" | "not-configured" | "error" };

// 현재 활성 SW 등록을 얻는다 (vite-plugin-pwa 가 자동 등록한 SW).
async function getReadyRegistration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

// 푸시 구독 + 서버 등록.
export async function subscribeUserPush(): Promise<SubscribeResult> {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    return { ok: false, reason: "not-configured" };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "denied" };
  }
  try {
    const reg = await getReadyRegistration();
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      }));
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      userAgent: navigator.userAgent,
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

// 푸시 구독 해제 + 서버 등록 해제.
export async function unsubscribeUserPush(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  const reg = await getReadyRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) {
    return;
  }
  const endpoint = sub.endpoint;
  try {
    await api.post("/push/unsubscribe", { endpoint });
  } finally {
    // 서버 해제 실패와 무관하게 브라우저 구독은 정리한다.
    await sub.unsubscribe();
  }
}

// 현재 이 브라우저가 구독 중인지.
export async function isCurrentlySubscribed(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }
  try {
    const reg = await getReadyRegistration();
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}
